/**
 * ============================================================================
 * DELIVERY APPROVAL COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Component for buyer to approve or dispute delivery
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransaction } from '../../hooks/useTransaction';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import Modal from '../common/Modal';
import DeliveryProof from './DeliveryProof';
import AutoReleaseCountdown from '../common/AutoReleaseCountdown';

/**
 * Delivery Approval Component
 * @param {Object} props
 * @param {string} props.transactionId - Transaction ID
 * @param {Object} props.transaction - Transaction object (optional)
 * @param {Function} props.onSuccess - Success callback
 * @returns {JSX.Element}
 */
const DeliveryApproval = ({ transactionId, transaction: propTransaction, onSuccess }) => {
  const navigate = useNavigate();
  const { getTransaction, approveDelivery, loading } = useTransaction();
  const { showSuccess, showError } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [transaction, setTransaction] = useState(propTransaction || null);
  const [isLoading, setIsLoading] = useState(!propTransaction);
  const [showApproveModal, setShowApproveModal] = useState(false);

  useEffect(() => {
    const loadTransaction = async () => {
      if (propTransaction) return;
      try {
        setIsLoading(true);
        const data = await getTransaction(transactionId);
        setTransaction(data);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement' : 'Failed to load transaction')
        );
        console.error('Load transaction error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (transactionId) {
      loadTransaction();
    }
  }, [transactionId, propTransaction, getTransaction, showError, language]);

  const handleApprove = async () => {
    try {
      await approveDelivery(transactionId);
      showSuccess(
        language === 'fr' ? 'Paiement libéré! Transaction complétée.' : 'Payment released! Transaction completed.'
      );
      setShowApproveModal(false);
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/transactions/${transactionId}`);
      }
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de l\'approbation' : 'Failed to approve delivery')
      );
    }
  };

  const handleRaiseIssue = () => {
    navigate(`/transactions/${transactionId}/dispute`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!transaction) {
    return null;
  }

  const deliverables = transaction.deliverablesUrl || transaction.deliverables || [];
  const deliverablesNotes = transaction.deliverablesNotes || transaction.deliveryNotes;

  return (
    <div className="max-w-4xl mx-auto">

        {/* Transaction Info */}
        <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {language === 'fr' ? 'Transaction' : 'Transaction'}
              </p>
              <p className="text-lg font-black text-gray-900 dark:text-white">
                {transaction.title}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {language === 'fr' ? 'Montant' : 'Amount'}
              </p>
              <p className="text-lg font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                {formatCurrency(transaction.amount || 0, transaction.currency || 'XAF')}
              </p>
            </div>
          </div>
        </Card>

        {/* Delivery Status */}
        {transaction.deliveredAt && (
          <Card variant="outlined" className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 mb-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-2xl shadow-lg">
                ✓
              </div>
              <div>
                <p className="font-black text-green-800 dark:text-green-200 text-lg">
                  {language === 'fr' ? 'Livré' : 'Delivered'}
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {formatDate(transaction.deliveredAt)}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Auto-Release Countdown */}
        {transaction.deliveredAt && transaction.autoReleaseAt && (
          <div className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
            <AutoReleaseCountdown
              deliveredAt={transaction.deliveredAt}
              autoReleaseAt={transaction.autoReleaseAt}
              onExpire={() => {
                getTransaction(transactionId).then(setTransaction);
              }}
            />
          </div>
        )}

        {/* Delivery Proof */}
        <div className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
          <DeliveryProof
            deliverables={deliverables}
            deliverablesNotes={deliverablesNotes}
          />
        </div>

        {/* Question */}
        <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.5s' }}>
          <h3 className="text-xl font-black text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <span className="text-2xl">❓</span>
            {language === 'fr' ? 'Êtes-vous satisfait?' : 'Are you satisfied?'}
          </h3>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-3 animate-fadeInUp" style={{ animationDelay: '0.6s' }}>
          <Button
            variant="gradient"
            size="lg"
            fullWidth
            onClick={() => setShowApproveModal(true)}
            loading={loading}
            className="bg-gradient-to-r from-green-600 to-emerald-600 shadow-xl hover:shadow-2xl transition-all text-lg py-6"
          >
            ✓ {language === 'fr' ? 'Libérer le paiement' : 'Release Payment'} →
          </Button>
          <Button
            variant="outline"
            size="lg"
            fullWidth
            onClick={handleRaiseIssue}
            className="border-2 border-yellow-500 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 font-semibold text-lg py-6"
          >
            ⚠ {language === 'fr' ? 'Soulever un problème' : 'Raise Issue'}
          </Button>
        </div>

        {/* Approve Confirmation Modal */}
        <Modal
          isOpen={showApproveModal}
          onClose={() => setShowApproveModal(false)}
          title={
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-xl">
                ✓
              </div>
              <span className="text-xl font-black bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {language === 'fr' ? 'Confirmer l\'approbation' : 'Confirm Approval'}
              </span>
            </div>
          }
        >
          <div className="space-y-6">
            <Card variant="outlined" className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  {language === 'fr' ? 'Montant à libérer' : 'Amount to Release'}
                </p>
                <p className="text-3xl font-black bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  {formatCurrency(transaction.amount || 0, transaction.currency || 'XAF')}
                </p>
              </div>
            </Card>

            <Card variant="outlined" className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⚠️</span>
                <div>
                  <p className="font-bold text-yellow-800 dark:text-yellow-200 mb-1">
                    {language === 'fr' ? 'Attention' : 'Warning'}
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    {language === 'fr'
                      ? 'Cette action libérera les fonds au vendeur. Cette action est irréversible.'
                      : 'This action will release funds to the seller. This action is irreversible.'}
                  </p>
                </div>
              </div>
            </Card>

            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setShowApproveModal(false)}
              >
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button
                variant="gradient"
                fullWidth
                onClick={handleApprove}
                loading={loading}
                className="bg-gradient-to-r from-green-600 to-emerald-600 shadow-xl hover:shadow-2xl transition-all"
              >
                {language === 'fr' ? 'Confirmer' : 'Confirm'} →
              </Button>
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default DeliveryApproval;
